/**
 * Sign-in. Shows whichever methods the API says are enabled (GET /auth/providers):
 *
 *   - identity providers such as Microsoft Entra ID, as links. The API runs the
 *     sign-in and comes back here only on failure (?error=...), otherwise to `next`;
 *   - the retiring administrator password, as a form, only while the API still has it.
 *
 * So this screen is correct before, during and after the move off the password
 * (docs/auth/PLANNING.md, Rollout) without a release of its own at each step.
 */
import { useState, type FormEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { TotumLogo } from '../components/Layout';
import { Button, ErrorNote, Field, Loading, buttonClass, inputClass } from '../components/ui';
import { API_ORIGIN } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApi } from '../lib/useApi';

interface SignInMethod {
  id: string;
  label: string;
  kind: 'redirect' | 'password';
}

/** What the API's sign-in callback can report, in words a person can act on. */
const SIGN_IN_ERRORS: Record<string, string> = {
  no_access: 'You signed in, but your account has not been given access to Totum Analytics. Ask an administrator to add you.',
  cancelled: 'Sign-in was cancelled.',
  expired: 'Sign-in took too long, or was started in another tab. Please try again.',
  failed: 'Sign-in did not complete. Please try again, and tell the Totum Analytics team if it keeps happening.',
};

export function Login() {
  const { me, login } = useAuth();
  const [params] = useSearchParams();
  const methods = useApi<SignInMethod[]>('/auth/providers');
  const [error, setError] = useState<{ message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const next = params.get('next');
  const destination = next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
  const returned = params.get('error');
  const returnedError = returned ? { message: SIGN_IN_ERRORS[returned] ?? (SIGN_IN_ERRORS.failed as string) } : null;

  if (me) return <Navigate to={destination} replace />;

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await login(String(form.get('email')), String(form.get('password')));
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(false);
    }
  }

  const redirects = methods.data?.filter((m) => m.kind === 'redirect') ?? [];
  const password = methods.data?.some((m) => m.kind === 'password') ?? false;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-black h-16 flex items-center px-6 gap-3">
        <TotumLogo />
        <span className="text-gray-400 text-sm border-l border-gray-700 pl-3">Analytics</span>
      </header>
      <main className="flex-1 flex items-start justify-center px-4 pt-[12vh]">
        <div className="w-full max-w-sm bg-white rounded-xl p-8 shadow-sm border border-gray-200">
          <h1 className="text-xl font-semibold text-gray-900">Sign in</h1>
          <p className="text-sm text-gray-600 mt-1 mb-6">Usage across Inovus sites and Totum applications.</p>
          {returnedError && <div className="mb-4"><ErrorNote error={returnedError} /></div>}

          {methods.error && <ErrorNote error={methods.error} onRetry={methods.reload} />}
          {!methods.data && !methods.error && <Loading label="Loading sign-in options" />}
          {methods.data && methods.data.length === 0 && (
            <p className="text-sm text-gray-600">No sign-in method is configured. Tell the Totum Analytics team.</p>
          )}

          {redirects.length > 0 && (
            <div className="space-y-3">
              {redirects.map((m) => (
                <a
                  key={m.id}
                  href={`${API_ORIGIN}/auth/login/${encodeURIComponent(m.id)}?next=${encodeURIComponent(destination)}&origin=${encodeURIComponent(window.location.origin)}`}
                  className={`${buttonClass('primary')} w-full justify-center`}
                >
                  {m.label}
                </a>
              ))}
            </div>
          )}

          {password && (
            <form onSubmit={submit} className={redirects.length > 0 ? 'mt-6 pt-6 border-t border-gray-200' : ''}>
              {redirects.length > 0 && <p className="text-xs text-gray-500 mb-4">Or, while it is still enabled, the administrator password:</p>}
              <Field label="Email" htmlFor="email">
                <input id="email" name="email" type="email" autoComplete="username" required className={inputClass} />
              </Field>
              <Field label="Password" htmlFor="password">
                <input id="password" name="password" type="password" autoComplete="current-password" required className={inputClass} />
              </Field>
              {error && <div className="mb-4"><ErrorNote error={error} /></div>}
              <Button type="submit" kind={redirects.length > 0 ? 'secondary' : 'primary'} disabled={busy} className="w-full justify-center">
                {busy ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
